const cloneBoundary = (boundary) => ({
  ...boundary,
  start: [...(boundary?.start || [])],
  end: [...(boundary?.end || [])],
});

const isEmptyBoundary = (boundary) =>
  boundary.start.length === 0 && boundary.end.length === 0;

/**
 * Adds a phrase selection, or removes it when the same type and range is
 * selected again. Phrase data is stored at both ends of the range, so both
 * entries must always be updated together.
 */
export const togglePhraseData = ({
  phraseData = {},
  phrase,
  startIndex,
  endIndex,
}) => {
  const nextPhraseData = { ...phraseData };
  const startBoundary = cloneBoundary(phraseData[startIndex]);
  const endBoundary =
    startIndex === endIndex
      ? startBoundary
      : cloneBoundary(phraseData[endIndex]);

  const matchesStart = (item) =>
    item.type === phrase.type && item.endIndex === endIndex;
  const matchesEnd = (item) =>
    item.type === phrase.type && item.startIndex === startIndex;

  // Treat a partially stored phrase as selected too. This lets a second click
  // repair legacy/inconsistent data instead of adding another duplicate.
  const isAlreadySelected =
    startBoundary.start.some(matchesStart) ||
    endBoundary.end.some(matchesEnd);

  if (isAlreadySelected) {
    startBoundary.start = startBoundary.start.filter(
      (item) => !matchesStart(item)
    );
    endBoundary.end = endBoundary.end.filter((item) => !matchesEnd(item));
  } else {
    startBoundary.start.push({ ...phrase, endIndex });
    startBoundary.start.sort((a, b) => b.endIndex - a.endIndex);

    endBoundary.end.push({ ...phrase, startIndex });
    endBoundary.end.sort((a, b) => b.startIndex - a.startIndex);
  }

  if (isEmptyBoundary(startBoundary)) {
    delete nextPhraseData[startIndex];
  } else {
    nextPhraseData[startIndex] = startBoundary;
  }

  if (startIndex !== endIndex) {
    if (isEmptyBoundary(endBoundary)) {
      delete nextPhraseData[endIndex];
    } else {
      nextPhraseData[endIndex] = endBoundary;
    }
  }

  return nextPhraseData;
};
